import React, { ReactElement, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import './LiveSettings.scss';
import { LiveProps } from '../../LiveContainer';
import {
  useGetJoinableEventListQuery,
  useGetLiveCategoryListQuery,
  useGetPrivateEventsMutation,
  useGetUserInfoQuery,
  usePutMirrorOnMutation,
  usePutMuteOffMutation,
  useRequestStartLiveMutation,
} from '../../../../services/api/apiProvider';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BarLoader from 'react-spinners/BarLoader';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useTranslation } from 'react-i18next';
import { ResponsePrivateEvent } from '../../../../services/api/models/response/responseEvent';
import { RequestStartLive } from '../../../../services/api/models/request/requestStartLive';

export interface MediaDevice {
  id: string;
  name: string;
  kind: MediaDeviceKind;
}

export const LiveSettings = (props: LiveProps): ReactElement => {
  const history = useHistory();
  const { data: liveCategory } = useGetLiveCategoryListQuery();
  const { data: listEvents } = useGetJoinableEventListQuery();
  // user data from query api
  const { data: user, isFetching } = useGetUserInfoQuery();
  const [getPrivateEvents] = useGetPrivateEventsMutation();

  const [startLive, { isLoading, isError }] = useRequestStartLiveMutation();
  const [turnOffMute] = usePutMuteOffMutation();
  const [turnOnMirror] = usePutMirrorOnMutation();

  const [eventId, setEventId] = useState<any>(localStorage.getItem('eventId'));

  const [name, setName] = useState<string | undefined | null>(
    localStorage.getItem('titleLive'),
  );

  const privateEvents = useRef<Array<ResponsePrivateEvent>>([]);

  const { t } = useTranslation();
  const formLabelStyle = { fontSize: '0.8rem' };

  let liveCategoryId = '';

  const onCloseModal = () => {
    // when close modal, go home if not in live
    if (!props.liveState.isInLive) {
      history.push('/');
    }
  };

  const handleClose = () => {
    props.toggleSettingsModal(false);
  };

  // update when user select live category
  const onLiveCategoryChange = (event: any) => {
    liveCategoryId = event.target.value;
  };

  // update live name
  const onLiveNameChange = (event: any) => {
    setName(event.target.value);
    localStorage.setItem('titleLive', event.target.value);
  };

  // update event
  const onSelectEvent = (event: any) => {
    let value = event.target.value;
    if (value == undefined || value === '--') value = null;
    localStorage.setItem('eventId', value);
    setEventId(value);
  };

  // update video input
  const onSelectVideoInput = (event: any) => {
    const value =
      event.target.value !== 'None' ? event.target.value : undefined;
    props.changeVideoInput(value);
  };

  // update audio input
  const onSelectAudioInput = (event: any) => {
    const value =
      event.target.value !== 'None' ? event.target.value : undefined;
    props.changeAudioInput(value);
  };

  // update audio output
  const onSelectAudioOutput = (event: any) => {
    props.changeAudioOutput(event.target.value);
  };

  const onTextBoxKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  // when user click start live
  const onClickStartLive = (): void => {
    if (!name || name.length <= 0) {
      toast.error('配信タイトルを設定してください。');
      return;
    }

    if (liveCategoryId === '') {
      liveCategoryId = (
        document.querySelector(
          '#live-setting__live-category',
        ) as HTMLInputElement
      ).value;
    }

    if (eventId === null) {
      let evId: string | null = (
        document.querySelector('#live-setting__event') as HTMLInputElement
      ).value;
      if (evId == undefined) evId = null;
      setEventId(evId);
    }

    let privateEventId = null;
    privateEvents?.current.forEach((event) => {
      if (Date.parse(event.endDateTime) > Date.now()) {
        privateEventId = event.id;
      }
    });

    let pairType: string | undefined = 'none';
    let kind: string | undefined = 'normal';
    if (!props.liveState.isLive) {
      props.changeVideoInput(undefined);
      pairType = 'none';
      kind = 'radio';
    }

    const requestStartLive: RequestStartLive = {
      liveHashtagIds: null,
      privateEventId: privateEventId,
      name,
      eventId,
      liveCategoryId,
      pairType: pairType,
      kind: kind,
    };

    // dispatch start live action
    startLive(requestStartLive)
      .unwrap()
      .then((responseLiveInfo) => {
        props.toggleSettingsModal(false);
        props.startLive(responseLiveInfo);
        turnOffMute(responseLiveInfo.id); // enable audio when start live by default
        turnOnMirror(responseLiveInfo.id); // enable mirror when start live by default
      });
  };

  useEffect((): void => {
    // redirect to top page if user is unverified
    if (user !== undefined && user.liverLeague !== 1) {
      alert(t('RequireVerified'));
      window.location.href = `${window.location.origin}/${process.env.REACT_APP_BASE_URL}`;
      return;
    }

    // load private event after load user
    if (user !== undefined) {
      getPrivateEvents(user.id)
        .unwrap()
        .then((result: ResponsePrivateEvent[]) => {
          privateEvents.current = result;
        });
    }

    // check video/audio permission and get IO devices
    AgoraRTC.getDevices()
      .then((devices) => {
        const listInputDevice = devices.map((device) => {
          const temp: MediaDevice = {
            id: device.deviceId,
            name: device.label,
            kind: device.kind,
          };
          return temp;
        });
        props.updateListMediaDevices(listInputDevice);
      })
      .catch(() => {
        toast.error('デバイスへの接続に失敗しました。');
      });
  }, [isFetching]);

  if (isLoading || isFetching) {
    return (
      <div className="d-flex justify-content-center">
        <BarLoader color={'#0275d8'} width="100%" cssOverride={{}}></BarLoader>
      </div>
    );
  }

  if (isError) {
    return <Alert variant="danger">Error</Alert>;
  }

  if (user !== undefined && user.liverLeague !== 1) {
    return <></>;
  }

  return (
    <>
      <Modal
        show={props.liveState.isShowSettingModal}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        centered
        scrollable={true}
      >
        <Modal.Header closeButton onHide={onCloseModal}></Modal.Header>
        <Modal.Body>
          <Form>
            {/* Title */}
            <Form.Label style={formLabelStyle}>タイトル</Form.Label>
            <Form.Control
              id="live-setting__live-name"
              placeholder="タイトル"
              value={name || ''}
              disabled={props.liveState.isInLive}
              onChange={(event) => onLiveNameChange(event)}
              onKeyDown={(event) => onTextBoxKeyDown(event)}
            />

            {/* Category */}
            <p></p>
            <Form.Label style={formLabelStyle}>カテゴリー</Form.Label>
            <Form.Select
              id="live-setting__live-category"
              value={props.liveState.liveData?.liveCategory?.id}
              disabled={props.liveState.isInLive}
              onChange={(event) => onLiveCategoryChange(event)}
            >
              {liveCategory?.map((category, index) => (
                <option key={index} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>

            {/* Events */}
            <p></p>
            <Form.Label style={formLabelStyle}>イベント</Form.Label>
            <Form.Select
              id="live-setting__event"
              value={eventId}
              disabled={props.liveState.isInLive}
              onChange={(event) => onSelectEvent(event)}
            >
              {listEvents?.map((event, index) => {
                if (index === 0 && eventId === null && event.id !== undefined) {
                  setEventId(event.id);
                  localStorage.setItem('eventId', event.id);
                }
                return (
                  <option key={index} value={event.id}>
                    {event.name}
                  </option>
                );
              })}
            </Form.Select>

            {/* Video input device */}
            <p></p>
            <Form.Label style={formLabelStyle}>カメラ選択</Form.Label>
            <Form.Select
              id="live-setting__video-input"
              onChange={(event) => onSelectVideoInput(event)}
              value={props.liveState.inputVideoDevice}
            >
              <>
                <option>None</option>
                {props.liveState.mediaDevices
                  .filter((device) => device.kind === 'videoinput')
                  .map((device, index) => (
                    <option key={index} value={device.id}>
                      {device.name}
                    </option>
                  ))}
              </>
            </Form.Select>

            {/* Audio input device */}
            <p></p>
            <Form.Label style={formLabelStyle}>オーディオの選択</Form.Label>
            <Form.Select
              id="live-setting__audio-input"
              onChange={(event) => onSelectAudioInput(event)}
              value={props.liveState.inputAudioDevice}
            >
              <>
                <option>None</option>
                {props.liveState.mediaDevices
                  .filter((device) => device.kind === 'audioinput')
                  .map((device, index) => (
                    <option key={index} value={device.id}>
                      {device.name}
                    </option>
                  ))}
              </>
            </Form.Select>

            {/* Audio output device */}
            <p></p>
            <Form.Label style={formLabelStyle}>スピーカー</Form.Label>
            <Form.Select
              id="live-setting__audio-output"
              onChange={(event) => onSelectAudioOutput(event)}
              value={props.liveState.outputAudioDevice}
            >
              {props.liveState.mediaDevices
                .filter((device) => device.kind === 'audiooutput')
                .map((device, index) => (
                  <option key={index} value={device.id}>
                    {device.name}
                  </option>
                ))}
            </Form.Select>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'center' }}>
          {!props.liveState.isInLive && (
            <Button variant="primary" onClick={onClickStartLive}>
              配信開始
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        limit={1}
        pauseOnHover={false}
      />
    </>
  );
};
